import { LogoutOutlined } from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import { destroyIMClient } from '@/pages/patient/components/patient-chat/service';
import { outLogin } from '@/services/ant-design-pro/api';
import { clearTokens, getRefreshToken } from '@/utils/token';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  const { styles } = useStyles();
  return (
    <span className={`anticon ${styles.avatarName}`}>
      {currentUser?.name || '用户'}
    </span>
  );
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'inline-flex',
      height: '32px',
      marginLeft: '16px',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 12px 0 0',
      cursor: 'pointer',
      borderRadius: '16px',
      border: '1px solid rgba(0, 0, 0, 0.1)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      background: '#fff',
      transition: 'all 0.3s',
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
        transform: 'translateY(-2px)',
      },
      '& > div': {
        display: 'flex',
        alignItems: 'center',
        height: '100%',
      },
      '& .ant-avatar': {
        flexShrink: 0,
      },
    },
    name: {
      marginLeft: '8px',
    },
    avatarName: {
      fontSize: '16px',
      fontWeight: 'bold',
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu: _menu,
  children,
}) => {
  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    try {
      // 获取refresh_token用于单设备登出
      const refreshToken = getRefreshToken();

      // 调用登出接口
      // 如果提供refresh_token，则单设备登出；如果不提供，则全设备登出
      await outLogin({
        refresh_token: refreshToken || undefined,
        logout_all_devices: !refreshToken, // 如果没有refresh_token，则登出所有设备
        urgent: false, // 普通登出，不立即撤销Access Token
      });
    } catch (error) {
      console.error('退出登录接口调用失败:', error);
      // 即使接口失败，也继续清除本地token
    }

    // 清理 IM 资源
    await destroyIMClient();

    // 清除本地存储的token
    clearTokens();

    // 退出登录后直接跳转到登录页，不保存 redirect 参数
    // 这样每个用户登录后都会进入 /dashboard 页面
    history.replace({
      pathname: '/user/login',
    });
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = async (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      await loginOut();
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || !currentUser.name) {
    return loading;
  }

  const menuItems = [
    // ...(menu
    //   ? [
    //       {
    //         key: 'center',
    //         icon: <UserOutlined />,
    //         label: '个人中心',
    //       },
    //       {
    //         key: 'settings',
    //         icon: <SettingOutlined />,
    //         label: '个人设置',
    //       },
    //       {
    //         type: 'divider' as const,
    //       },
    //     ]
    //   : []),
    {
      key: 'logout',
      icon: <LogoutOutlined style={{ color: '#ff4d4f' }} />,
      label: <span style={{ color: '#ff4d4f' }}>退出登录</span>,
    },
  ];

  return (
    <HeaderDropdown
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      <span className={styles.action}>{children}</span>
    </HeaderDropdown>
  );
};
